import React, { useMemo } from 'react';
import { Box, Chip, IconButton, Tooltip, useTheme } from '@mui/material';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAppearance } from '../../theme/AppearanceProvider';
import { ProviderSelect } from '../../components/ProviderSelect';
import { FilterToolbar } from '../../components/FilterToolbar';
import type { TopFilterConfig } from '../../components/TopFilters';
import type { SharedView, ViewTemplate } from '../../hooks/useSharedViews';
import type { FacetsState } from './useDynamicColumns';
import type { ReportFiltersState, ProviderSource, SavedViewState } from './viewState';

type SavedView = SavedViewState;

export interface ReportTopToolbarProps {
  // Provider
  providerSource: ProviderSource;
  // onProviderChange: (next: 'opentrack' | 'dealer_vault' | 'both') => void;
  onProviderChange: (next: ProviderSource) => void;
  dateRange: { startDate: string; endDate: string };
  tenantId?: string;
  loading: boolean;
  isInitialLoad: boolean;

  // Facets for filter options
  facets: FacetsState;

  // Filter state
  filterState: ReportFiltersState;
  onTypeCodeFilterChange: (next: string[]) => void;
  onLineTypeFilterChange: (next: string[]) => void;
  onLinePayFilterChange: (next: string[]) => void;
  onCoveredLineFilterChange: (next: string[]) => void;

  // Date
  onDateChange: (date: { startDate: string; endDate: string }) => void;

  // Export
  exportQueryParams: URLSearchParams;

  // Display toggles
  zebraRows: boolean;
  onZebraRowsChange: (v: boolean) => void;
  compactHeaders: boolean;
  onCompactHeadersChange: (v: boolean) => void;

  // Views
  savedViews: SavedView[];
  selectedViewName: string;
  onViewChange: (name: string) => void;
  onSaveView: (payload: { name: string; description?: string; share?: boolean }) => Promise<void>;
  onDeleteView: () => Promise<void> | void;
  onReset: () => void;

  // Shared views
  sharedViews: SharedView[];
  viewTemplates: ViewTemplate[];
  canShareViews: boolean;
  onSaveSharedView: (name: string, description?: string) => Promise<void>;
  onApplySharedView: (view: SharedView | ViewTemplate, type: 'shared' | 'template') => void;
  tenantName?: string | null;
}

/**
 * Top toolbar for the OLSG Daily Report (Reports.tsx).
 * Renders provider toggle, theme toggle, and FilterToolbar.
 * Extracted from the renderTopToolbarCustomActions callback.
 */
export const ReportTopToolbar = React.memo<ReportTopToolbarProps>(({
  providerSource,
  onProviderChange,
  dateRange,
  tenantId,
  loading,
  isInitialLoad,
  facets,
  filterState,
  onTypeCodeFilterChange,
  onLineTypeFilterChange,
  onLinePayFilterChange,
  onCoveredLineFilterChange,
  onDateChange,
  exportQueryParams,
  zebraRows,
  onZebraRowsChange,
  compactHeaders,
  onCompactHeadersChange,
  savedViews,
  selectedViewName,
  onViewChange,
  onSaveView,
  onDeleteView,
  onReset,
  sharedViews,
  viewTemplates,
  canShareViews,
  onSaveSharedView,
  onApplySharedView,
  tenantName,
}) => {
  const theme = useTheme();
  const { mode, setMode } = useAppearance();

  const filters: TopFilterConfig[] = useMemo(() => [
    {
      id: 'typeCode',
      label: 'Type Code',
      options: (facets.typeCode?.length
        ? facets.typeCode.map((f) => f.value)
        : ['0', 'I', '']) as string[],
      value: filterState.typeCodeFilter,
      onChange: onTypeCodeFilterChange,
    },
    {
      id: 'lineType',
      label: 'Line Type',
      options: facets.lineType?.length
        ? facets.lineType.map((f) => f.value)
        : ['A', 'L', '', 'I', 'W', 'MR'],
      value: filterState.lineTypeFilter,
      onChange: onLineTypeFilterChange,
    },
    {
      id: 'linePay',
      label: 'Line Pay',
      options: facets.linePay?.length
        ? facets.linePay.map((f) => f.value)
        : ['C', 'I', 'W', 'MR', '', 'CP', 'TT'],
      value: filterState.linePayFilter,
      onChange: onLinePayFilterChange,
    },
    {
      id: 'coveredLine',
      label: 'Covered Line',
      options: ['true', 'false', ''],
      value: filterState.coveredLineFilter,
      onChange: onCoveredLineFilterChange,
      renderOption: (val: string) =>
        val === 'true' ? 'Covered' : val === 'false' ? 'Not Covered' : '(blank)',
      minWidth: 140,
      maxWidth: 160,
    },
  ], [
    facets.typeCode, facets.lineType, facets.linePay,
    filterState.typeCodeFilter, filterState.lineTypeFilter,
    filterState.linePayFilter, filterState.coveredLineFilter,
    onTypeCodeFilterChange, onLineTypeFilterChange,
    onLinePayFilterChange, onCoveredLineFilterChange,
  ]);

  return (
    <>
      {/* Compact Header Row with all primary controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBottom: 2,
          padding: '12px 16px',
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
          borderRadius: 1,
          flexWrap: 'wrap',
        }}
      >
        {/* Data Source Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ProviderSelect
            value={providerSource}
            onChange={(v) => onProviderChange(v as ProviderSource)}
            dateRange={dateRange}
            tenantId={tenantId}
            loading={loading && isInitialLoad}
          />
        </Box>

        {/* Vertical Divider */}
        <Box
          sx={{
            width: '1px',
            height: '40px',
            bgcolor:
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
          }}
        />

        {/* Theme Toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginLeft: 'auto' }}>
          
        </Box>
      </Box>

      {/* Filters Toolbar */}
      <FilterToolbar
        filters={filters}
        date={dateRange}
        onDateChange={onDateChange}
        queryParams={exportQueryParams}
        loading={loading}
        zebraRows={zebraRows}
        onZebraRowsChange={onZebraRowsChange}
        compactHeaders={compactHeaders}
        onCompactHeadersChange={onCompactHeadersChange}
        savedViews={savedViews}
        selectedViewName={selectedViewName}
        onViewChange={onViewChange}
        onSaveView={onSaveView}
        onDeleteView={onDeleteView}
        onReset={onReset}
        sharedViews={sharedViews}
        viewTemplates={viewTemplates}
        canShareViews={canShareViews}
        onSaveSharedView={onSaveSharedView}
        onApplySharedView={onApplySharedView}
        tenantName={tenantName}
      />
    </>
  );
});

ReportTopToolbar.displayName = 'ReportTopToolbar';
